import { Injectable } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CognitoUserPool, CognitoUserSession, CognitoUser } from 'amazon-cognito-identity-js';
import { CognitoIdentityCredentials, config } from 'aws-sdk';
import { User } from '../../model';
import { of as observableOf } from 'rxjs';
import { HelperService } from 'src/app/services';
import { HTTP_HEADER_MODULE_CODE } from 'src/app/utils/constant';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private _whenLoggedIn = new BehaviorSubject<User>(null);
  private _userPool: CognitoUserPool;
  private _user: User;

  get whenLoggedIn(){ return this._whenLoggedIn };
  get user(){ return this._user }
  get userPool(){ return this._userPool }

  constructor(
    private helper: HelperService,
    private router: Router
  ) {
    this._userPool = new CognitoUserPool({
      UserPoolId: environment.cognitoUserPoolId,
      ClientId: environment.cognitoAppClientId
    });
    
    this.initAWSCredentials();
  }

  private setUser(user: User){
    this._user = user
    this._whenLoggedIn.next(user);
  }

  public setUserData(user: any){
    localStorage.setItem('UserData', JSON.stringify(user))
  }

  public setUserSettings(settings: any){
    localStorage.setItem('Settings', JSON.stringify(settings))
  }

  public getUserData(){
    return JSON.parse(localStorage.getItem('UserData'))
  }

  public getUserSettings(){
    return JSON.parse(localStorage.getItem('Settings'))
  }

  public setCountryIsAllowed(val: boolean) {
    localStorage.setItem('CountryRetriction', val ? "1" : null)
  }

  public getCountryIsAllowed() {
    return localStorage.getItem('CountryRetriction') == "1" ? true : false
  }

  public initAWSCredentials(){
    let cognitoUser = this.userPool.getCurrentUser();
    //console.log(cognitoUser)

    if(!cognitoUser){
      return;
    }

    this.setUser(new User(cognitoUser));

    cognitoUser.getSession(async (err, session: CognitoUserSession) => {
      if(err){
        if(err.code === "NetworkError"){
          this.helper.showError("TIMEOUT_ERR")
        }
        this.router.navigate(['/'])
        return console.log(err);
      }
      let logins = {
        [environment.cognitoIdentityEndpoint]: session.getIdToken().getJwtToken()
      };
  
      let creds = new CognitoIdentityCredentials({
        IdentityPoolId: environment.cognitoIdentityPoolId,
        Logins: logins
      },{
        region: environment.region
      });
  
      config.credentials = creds;

      await ((config.credentials) as CognitoIdentityCredentials).refreshPromise();

      if(!environment.production){console.log('%c' + 'session.getIdToken().getJwtToken()', 'background: #bada55; color: #222');console.log(session.getIdToken().getJwtToken() ? session.getIdToken().getJwtToken() : null)};
    });
  }

  public checkAWSCredentials(callback: (err?) => void){
    let cognitoUser = this.userPool.getCurrentUser();
  
    if(!cognitoUser){
      return callback(true);
    }

    if(((config.credentials) as CognitoIdentityCredentials).needsRefresh()){
  
      cognitoUser.getSession((err, session: CognitoUserSession) => {
        if(err){
          console.log(err);
          return callback(err);
        }

        cognitoUser.refreshSession(session.getRefreshToken(), (err, session: CognitoUserSession) => {
          if(err){
            console.log(err);
            return callback(err);
          }
          
          let logins = {
            [environment.cognitoIdentityEndpoint]: session.getIdToken().getJwtToken()
          };
      
          let creds = new CognitoIdentityCredentials({
            IdentityPoolId: environment.cognitoIdentityPoolId,
            Logins: logins
          },{
            region: environment.region
          });
      
          config.credentials = creds;
    
          ((config.credentials) as CognitoIdentityCredentials).refresh((err) => {
            if(err){
              console.log(err);
              return callback(err);
            }
            callback();
          });
        });
        
      });
    } else {
      callback();
    }
  }

  public logout(){
    let cognitoUser = this.userPool.getCurrentUser();
    if(cognitoUser){
      cognitoUser.signOut();
    }

    // Clear Cognito Cache ID and re-initialize
    if (config.credentials) {
      ((config.credentials) as CognitoIdentityCredentials).clearCachedId();
      let creds = new CognitoIdentityCredentials({
        IdentityPoolId: environment.cognitoIdentityPoolId
      },{
        region: environment.region
      });      
      config.credentials = creds;
    }
    this.setUser(null);
    this.clearSettings();
  }

  public clearSettings() {
    const reinstate = sessionStorage.getItem('initView') == "1";

    sessionStorage.clear();
    localStorage.clear();

    // we reinstate is the previous value is "1"
    if (reinstate) {
      sessionStorage.setItem('initView', "1")
    }
  }

  public isSignUpAllowed():boolean {
    return environment.allowSignup
  }

  public isLogInAllowed(): boolean{
    return environment.allowLogin
  }
}
